import graphql__383541cb0bf6406a25f6 from "./__generated__/SubscriptionViewUpdateCustomerOrderNotesMutation.graphql.ts";
import graphql__31cd2df90685da7e880e from "./__generated__/SubscriptionViewUpdatePurchaseOrderReferenceMutation.graphql.ts";
import graphql__6e9f62c43d11a4221989 from "./__generated__/SubscriptionViewQuery.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import Panel from "./presentational/Panel";
import BreadcrumbNavigation from "./navigation/BreadcrumbNavigation";
import {loadQuery, usePreloadedQuery, useRelayEnvironment} from "react-relay/hooks";
import type {SubscriptionViewQuery as SubscriptionViewQueryType} from "./__generated__/SubscriptionViewQuery.graphql";
import SubscriptionViewQuery from "./__generated__/SubscriptionViewQuery.graphql";
import SubscriptionHeader from "./SubscriptionHeader";
import ActionButton from "./presentational/button/ActionButton";
import TerminateSubscriptionDialog from "./dialog/TerminateSubscriptionDialog";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import {faHourglassClock, faHourglassEnd} from "@fortawesome/pro-regular-svg-icons";
import CancelSubscriptionDialog from "./dialog/CancelSubscriptionDialog";
import EditableDataListItem from "./form/EditableDataListItem";
import EditableDataListField from "./form/EditableDataListField";
import * as yup from "yup";
import {Link} from "react-router-dom";
import {useErrorContext} from "../contexts/ErrorContext";
import {commitMutationWithErrorHandling} from "../helper/commitMutationWithErrorHandling";
import {useIntl} from "react-intl";

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type SubscriptionViewParams = {
    prepared: any
};
type SubscriptionViewProps = SubscriptionViewParams & ConnectedProps<typeof connector>;

const handleUpdateCustomerOrderNotesSubmission = (input, setEditingActive, relayEnvironment, addError, intl) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__383541cb0bf6406a25f6,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: intl.formatMessage({id: 'errors.failedToUpdateCustomerOrderNotes'})
        }
    )
}

const handleUpdatePurchaseOrderReferenceSubmission = (input, setEditingActive, relayEnvironment, addError, intl) => {
    commitMutationWithErrorHandling(
        relayEnvironment,
        {
            mutation: graphql__31cd2df90685da7e880e,
            variables: {
                input: input
            },
            onCompleted: (response, errors) => {
                setEditingActive(false);
            },
            onError: (error) => {
                setEditingActive(false);
            }
        },
        addError,
        {
            customErrorMessage: intl.formatMessage({id: 'errors.failedToUpdatePurchaseOrderReference'})
        }
    )
}


const SubscriptionView: React.FC<SubscriptionViewProps> = (props) => {
    const intl = useIntl();

    const {subscription} = usePreloadedQuery<SubscriptionViewQueryType>(
        graphql__6e9f62c43d11a4221989,
        props.prepared.subscriptionViewQuery
    );

    const relayEnvironment = useRelayEnvironment();
    const {addError} = useErrorContext();

    if (subscription == null) {
        return <p>{intl.formatMessage({id: 'view.subscription.notFound'})}</p>;
    }

    const customerOrderNotesValidationSchema = yup.object().shape({
        vatId: yup.string()
            .max(255, intl.formatMessage({id: 'validation.field.orderNotesTooLong'}))
            .matches(/^[-+&\p{L}\p{P}\d .]{0,255}$/u, intl.formatMessage({id: 'validation.field.invalidOrderNotes'}))
            .nullable()
    });

    const purchaseOrderReferenceValidationSchema = yup.object().shape({
        vatId: yup.string()
            .matches(/^[-+&\p{L}\p{P}\d .]{0,20}$/u, intl.formatMessage({id: 'validation.field.invalidPurchaseOrderReference'}))
            .nullable()
    });

    const breadcrumbNavigationItems = [
        {label: intl.formatMessage({id: 'view.subscriptions.headline'}), to: '/subscriptions'},
        {label: subscription.id, to: '/subscriptions/' + subscription.id},
    ]

    const {dispatchShowModal, dispatchHideModal} = props

    const showTerminateSubscriptionDialog = (subscription) => {
        dispatchShowModal({
            body: (<TerminateSubscriptionDialog queryRef={subscription} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    const showCancelSubscriptionDialog = (subscription) => {
        dispatchShowModal({
            body: (<CancelSubscriptionDialog queryRef={subscription} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    // FIXME: How can we handle GraphQL DateTime correctly?
    const formattedExpiresAt = subscription.expiresAt ? subscription.expiresAt.substring(0, 10) : "";
    const formattedActivatedAt = subscription.activatedAt ? subscription.activatedAt.substring(0, 10) : "";

    return (
        <article>
            <BreadcrumbNavigation items={breadcrumbNavigationItems}/>
            <SubscriptionHeader subscription={subscription}/>
            <div className="mt-8"/>
            <div className="mx-auto lg:px-8 grid grid-cols-1 gap-x-4 gap-y-8">
                <Panel headline={intl.formatMessage({id: 'view.subscription.panel.headline'})} buttons={
                    <>
                        {subscription.state === 'ACTIVE' && (<ActionButton icon={faHourglassEnd} iconSize={"lg"} title={intl.formatMessage({id: 'view.subscriptions.terminateButton'})} onClick={e => showTerminateSubscriptionDialog(subscription)}/>)}
                        {subscription.state === 'ACTIVE' && (<ActionButton icon={faHourglassClock} iconSize={"lg"} title={intl.formatMessage({id: 'view.subscriptions.cancelButton'})} onClick={e => showCancelSubscriptionDialog(subscription)}/>)}
                    </>
                }>
                    <dl className="divide-y divide-gray-200">
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">{intl.formatMessage({id: 'view.subscription.status'})}</dt>
                            <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <span className="grow">
                                    {subscription.state === 'EXPIRED' ? intl.formatMessage({id: 'view.subscription.status.expiredAt'}, {date: formattedExpiresAt}) : ""}
                                    {subscription.state === 'CANCELED' ? intl.formatMessage({id: 'view.subscription.status.canceledWillExpireAt'}, {date: formattedExpiresAt}) : ""}
                                    {subscription.state === 'ACTIVE' ? intl.formatMessage({id: 'view.subscription.status.activeSince'}, {date: formattedActivatedAt}) : ""}
                                </span>
                            </dd>
                        </div>
                    </dl>
                    <dl className="divide-y divide-gray-200">
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">{intl.formatMessage({id: 'view.subscription.customerAccount'})}</dt>
                            <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <Link to={"/customers/" + subscription.customerAccount.id} className="focus:outline-hidden">
                                    <span className="grow" title={subscription.customerAccount.id}>{subscription.customerAccount.customerName}</span>
                                </Link>
                            </dd>
                        </div>
                    </dl>
                    <dl className="divide-y divide-gray-200">
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">{intl.formatMessage({id: 'view.subscription.service'})}</dt>
                            <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <span className="grow" title={subscription.subscriptionService.id}>{subscription.subscriptionService.name}</span>
                            </dd>
                        </div>
                    </dl>
                    <dl className="divide-y divide-gray-200">
                        <div className="py-4 sm:py-5 sm:grid sm:grid-cols-3 sm:gap-4">
                            <dt className="text-sm font-medium text-gray-500">{intl.formatMessage({id: 'view.subscription.serviceReference'})}</dt>
                            <dd className="mt-1 flex text-sm text-gray-900 sm:mt-0 sm:col-span-2">
                                <span className="grow" title={subscription.subscriptionServiceReference}><a href={subscription.subscriptionServiceReferenceUri ?? "#"} target="beach">{subscription.subscriptionServiceReferenceLabel}</a></span>
                            </dd>
                        </div>
                    </dl>
                    <EditableDataListItem
                        label={intl.formatMessage({id: 'subscription.customerOrderNotes'})}
                        fields={(<EditableDataListField type="text" name="customerOrderNotes" placeholder={intl.formatMessage({id: 'subscription.customerOrderNotes.editPlaceholder'})}/>)}
                        initialValues={{subscriptionIdentifier: subscription.id, customerOrderNotes: subscription.customerOrderNotes ?? ""}}
                        validationSchema={customerOrderNotesValidationSchema}
                        handleSubmission={(values, setEditingActive) => handleUpdateCustomerOrderNotesSubmission(values, setEditingActive, relayEnvironment, addError, intl)}
                    >
                        {subscription.customerOrderNotes}
                    </EditableDataListItem>
                    <EditableDataListItem
                        label={intl.formatMessage({id: 'subscription.purchaseOrderReference'})}
                        fields={(<EditableDataListField type="text" name="purchaseOrderReference" placeholder={intl.formatMessage({id: 'subscription.purchaseOrderReference.editPlaceholder'})}/>)}
                        initialValues={{subscriptionIdentifier: subscription.id, purchaseOrderReference: subscription.purchaseOrderReference ?? ""}}
                        validationSchema={purchaseOrderReferenceValidationSchema}
                        handleSubmission={(values, setEditingActive) => handleUpdatePurchaseOrderReferenceSubmission(values, setEditingActive, relayEnvironment, addError, intl)}
                    >
                        {subscription.purchaseOrderReference}
                    </EditableDataListItem>
                </Panel>
            </div>
        </article>
    );
}

export default connector(SubscriptionView);

function prepare_SubscriptionView(params, relayEnvironment) {
    return {
        subscriptionViewQuery: loadQuery(
            relayEnvironment,
            SubscriptionViewQuery,
            {
                id: params.id,
                language: params.language
            },
            {fetchPolicy: 'store-and-network'},
        ),
    }
}

export {prepare_SubscriptionView};
